/**
 * DEV-ONLY offline mock for the Finance dashboard redesign.
 *
 * Enabled ONLY when VUE_APP_FINANCE_MOCK === "true" (set in
 * .env.development.local). When active it:
 *   1. marks the user as an authenticated `finance` role so the router guard
 *      lets us into the finance pages without a real login / backend;
 *   2. swaps the axios adapter on the shared `api` / `apiV2` instances so every
 *      request resolves from local fixtures instead of hitting the network.
 *
 * Production builds never import this (guarded in main.js by the env flag), so
 * there is zero runtime cost or leakage outside local development.
 */
import api from "@/apiConfig/api";
import apiV2 from "@/apiConfig/api-v2";
import store from "@/store";
import router from "@/router/router";
import { encryptText } from "@/utils/cryptFunc";
import { DateTime } from "luxon";
import * as fx from "./financeFixtures";

export const FINANCE_MOCK_ON = process.env.VUE_APP_FINANCE_MOCK === "true";

const ok = (data, config) =>
  Promise.resolve({
    data,
    status: 200,
    statusText: "OK",
    headers: {},
    config,
    request: {},
  });

function mockAdapter(config) {
  const fullUrl = (config.baseURL || "") + (config.url || "");
  const path = fullUrl.split("?")[0];

  if (path.endsWith("/users/salary")) return ok(fx.employees, config);
  // Both branch sources used across finance widgets resolve to the same list.
  if (path.endsWith("/company") || path.endsWith("/coins/student/branches"))
    return ok(fx.branches, config);
  if (path.endsWith("/me"))
    return ok({ id: 9001, first_name: "Murad", last_name: "Finance", role: 0 }, config);
  if (path.includes("/finance/salary/group/")) {
    const gid = path.split("/finance/salary/group/")[1];
    return ok(fx.getGroupDetail(gid), config);
  }
  if (path.endsWith("/reports/teacher/ratings")) return ok(fx.ratings, config);
  if (path.endsWith("/reports/teacher/group-ratings")) {
    const tid =
      (config.params && config.params.teacher_id) ||
      new URLSearchParams(fullUrl.split("?")[1] || "").get("teacher_id");
    return ok(fx.getGroupRatings(tid), config);
  }
  if (path.endsWith("/finance/course-rates") || path.endsWith("/finance/salary/course-rates"))
    return ok(fx.courseRates, config);
  if (path.endsWith("/users/salary/recalculation-history"))
    return ok(fx.recalcInfo, config);

  // Auth endpoints — always "succeed" so nothing logs us out.
  if (path.includes("/auth/")) return ok({}, config);

  // Anything else: harmless empty payload so unrelated widgets don't crash.
  return ok([], config);
}

function seedSession() {
  // Auth state (cookie-based in prod) — short-circuit the router guard.
  store.commit("auths/setAuthenticated", true);
  store.commit("auths/setAuthChecked");

  // Identity shown in the header + role used by the guard / sidebar.
  localStorage.setItem("role", encryptText("finance"));
  localStorage.setItem("firstname", "Murad");
  localStorage.setItem("lastname", "Finance");
  if (!localStorage.getItem("lang")) localStorage.setItem("lang", "uz");

  // Default the dashboard to 2026-May (matches the reference screenshots).
  if (!localStorage.getItem("finance-selected-month")) {
    localStorage.setItem("finance-selected-month", "2026-05-01");
  }
  const d = DateTime.fromISO(
    localStorage.getItem("finance-selected-month") || "2026-05-01"
  );
  localStorage.setItem("fromDate", d.startOf("month").toFormat("yyyy-MM-dd"));
  localStorage.setItem("tillDate", d.endOf("month").toFormat("yyyy-MM-dd"));
}

// Deep-link support: when an employee-info / salary-info page is opened
// directly (refresh or pasted URL) the localStorage cache the page relies on
// hasn't been primed by a row click — seed it from fixtures here.
function seedRouteContext(to) {
  if (to.name === "finance-employee-info") {
    const emp = fx.findEmployee(to.params.user_id);
    if (emp) localStorage.setItem("employee", JSON.stringify(emp));
  } else if (to.name === "finance-salary-info") {
    const meta = fx.getGroupMeta(to.params.group_id);
    if (meta) {
      localStorage.setItem("group_name", meta.group_name);
      localStorage.setItem("teacher_id", String(meta.teacher_id));
      const emp = fx.findEmployee(meta.teacher_id);
      if (emp) localStorage.setItem("employee", JSON.stringify(emp));
    }
  }
}

export function installFinanceMock() {
  if (!FINANCE_MOCK_ON) return;
  seedSession();
  api.defaults.adapter = mockAdapter;
  apiV2.defaults.adapter = mockAdapter;
  router.beforeEach((to, from, next) => {
    seedRouteContext(to);
    next();
  });
  // eslint-disable-next-line no-console
  console.info(
    "%c[finance-mock] offline fixtures active — no backend needed",
    "color:#08c88b;font-weight:bold"
  );
}
